// React Hooks:
import React, { useState, useEffect } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  getSubReportsListAction,
  subReportsModal,
} from "../subReportsModalSlice";
import { authentication } from "../../../authentication/authenticationSlice";
import { subReportsModalOpenAction } from "../subReportsModalSlice";
// MUI Components:
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  SvgIcon,
  Typography,
  Skeleton,
  useTheme,
  Tooltip,
} from "@mui/material";
// MUI Styles:
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { tableCellClasses } from "@mui/material/TableCell";
// Icones:
import { ReactComponent as SquareCheckIcon } from "../../../../assets/icons/squareCheck-icon.svg";
import { ReactComponent as SquareRemoveIcon } from "../../../../assets/icons/squareRemove-icon.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow-right-icon.svg";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: "14px",
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.text.primary,
    padding: "2px 2px 2px 24px",
    height: "65px",
    borderBottom: "none",
    [theme.breakpoints.down("lg")]: {
      height: "60px",
      fontSize: "12px",
      padding: "2px 2px 2px 14px",
    },
    [theme.breakpoints.down("md")]: {
      height: "55px",
      fontSize: "10px",
      padding: "2px 2px 2px 4px",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    maxWidth: 100,
    fontSize: "14px",
    padding: "2px 2px 2px 24px",
    height: "65px",
    borderBottom: "none",
    color: "inherit",
    [theme.breakpoints.down("lg")]: {
      height: "60px",
      fontSize: "12px",
      padding: "2px 2px 2px 14px",
    },
    [theme.breakpoints.down("md")]: {
      height: "55px",
      fontSize: "10px",
      padding: "2px 2px 2px 4px",
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.background.paper,
  },
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.background.paper,
    filter: "brightness(95%)",
  },
  color: "#A1A2A3",
  transition: "background 0.3s",
  "&:hover": {
    backgroundColor: "#e1e1e1",
    color: "inherit",
  },
}));

const useStyles = makeStyles((theme) => ({
  tableBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  tableRoot: {
    "& th:last-child": {
      borderTopRightRadius: "8px",
    },
    "& tr:last-child": {
      borderRadius: "8px",
    },
    "& th:first-child": {
      borderTopLeftRadius: "8px",
    },
  },
  linkBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "4px",
      height: "4px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

const skeletonItems = [1, 2, 3, 4, 5, 6, 7];

const SubReportsModalTabel = () => {
  const classes = useStyles();
  const theme = useTheme();

  const { token } = useAppSelector(authentication);
  const {
    subReportsUniqId,
    subReportsData: { actionStatus, list },
  } = useAppSelector(subReportsModal);

  const dispatch = useAppDispatch();

  const [sort, setSort] = useState({
    firstName: "right",
    download: "right",
    status: "right",
  });

  useEffect(() => {
    if (!!token) {
      dispatch(getSubReportsListAction({ id: subReportsUniqId }));
    }
  }, [dispatch, subReportsUniqId, token]);

  const sortHandler = (name: string) => {
    switch (name) {
      case "firstName":
        setSort((last) => ({
          ...last,
          firstName:
            last.firstName === "right"
              ? "down"
              : last.firstName === "down"
              ? "up"
              : last.firstName === "up"
              ? "right"
              : "",
          download: "right",
          status: "right",
        }));
        break;
      case "download":
        setSort((last) => ({
          ...last,
          download:
            last.download === "right"
              ? "down"
              : last.download === "down"
              ? "up"
              : last.download === "up"
              ? "right"
              : "",
          firstName: "right",
          status: "right",
        }));
        break;
      case "status":
        setSort((last) => ({
          ...last,
          status:
            last.status === "right"
              ? "down"
              : last.status === "down"
              ? "up"
              : last.status === "up"
              ? "right"
              : "",
          firstName: "right",
          download: "right",
        }));
        break;
      default:
        break;
    }
  };

  const sortSvgIcon = (sort: string) => {
    switch (sort) {
      case "down":
        return "rotate(90deg)";
      case "up":
        return "rotate(-90deg)";
      case "right":
        return "rotate(0deg)";
      default:
        return;
    }
  };

  const sortFunction = (
    items: any,
    variableItem: string,
    direction: string
  ) => {
    let firstSort;
    let secondSort;

    switch (direction) {
      case "a-z":
        firstSort = [...items].sort((a: any, b: any) => {
          const x = a[`${variableItem}`];
          const y = b[`${variableItem}`];

          return x < y ? -1 : x > y ? 1 : 0;
        });

        secondSort = [...firstSort].sort((a: any, b: any) => {
          const x = a[`${variableItem}`].toLowerCase();
          const y = b[`${variableItem}`].toLowerCase();

          return x < y ? -1 : x > y ? 1 : 0;
        });

        return secondSort;

      case "z-a":
        firstSort = [...items].sort((a: any, b: any) => {
          const x = a[`${variableItem}`];
          const y = b[`${variableItem}`];

          return x < y ? 1 : x > y ? -1 : 0;
        });

        secondSort = [...firstSort].sort((a: any, b: any) => {
          const x = a[`${variableItem}`].toLowerCase();
          const y = b[`${variableItem}`].toLowerCase();

          return x < y ? 1 : x > y ? -1 : 0;
        });

        return secondSort;

      default:
        return items;
    }
  };

  const sortItems = (items: any) => {
    if (sort.firstName === "down") {
      return sortFunction(items, "name", "a-z");
    }

    if (sort.firstName === "up") {
      return sortFunction(items, "name", "z-a");
    }

    if (sort.download === "down") {
      const trueDownload = [...items].filter(
        (item: any) => item.download === true
      );
      const falseDownload = [...items].filter(
        (item: any) => item.download === false
      );

      return [...trueDownload, ...falseDownload];
    }
    if (sort.download === "up") {
      const trueDownload = [...items].filter(
        (item: any) => item.download === true
      );
      const falseDownload = [...items].filter(
        (item: any) => item.download === false
      );

      return [...falseDownload, ...trueDownload];
    }

    if (sort.status === "down") {
      const viewed = [...items].filter((item: any) => item.status === true);
      const notViewed = [...items].filter((item: any) => item.status === false);

      return [...viewed, ...notViewed];
    }

    if (sort.status === "up") {
      const viewed = [...items].filter((item: any) => item.status === true);
      const notViewed = [...items].filter((item: any) => item.status === false);

      return [...notViewed, ...viewed];
    }

    return items;
  };

  return (
    <TableContainer
      component={Box}
      sx={{
        minHeight: "130px",
        maxHeight: "520px",
        width: "100%",
        borderRadius: "8px",
        boxShadow: 12,
        [theme.breakpoints.down("lg")]: {
          minHeight: "120px",
          maxHeight: "360px",
        },
        [theme.breakpoints.down("md")]: {
          minHeight: "110px",
          maxHeight: "275px",
        },
        [theme.breakpoints.down("sm")]: {
          minHeight: "110px",
          maxHeight: "220px",
        },
      }}
      className={classes.tableBox}
    >
      <Table stickyHeader className={classes.tableRoot}>
        <TableHead>
          <TableRow>
            <StyledTableCell align="left" width="4%"></StyledTableCell>
            <StyledTableCell align="left" width="16%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.firstName),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("firstName")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Name
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="12%">
              <Typography
                sx={{
                  fontSize: "inherit",
                  color: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                IP Address
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left" width="12%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.download),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("download")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Download
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="12%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.status),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("status")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="16%">
              <Typography
                sx={{
                  fontSize: "inherit",
                  color: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                Date
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left" width="16%">
              <Typography
                sx={{
                  fontSize: "inherit",
                  color: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                Browser
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left" width="12%">
              <Typography
                sx={{
                  fontSize: "inherit",
                  color: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                OS
              </Typography>
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {actionStatus === "loading"
            ? skeletonItems.map((row: any, index: number) => (
                <StyledTableRow key={index}>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "100%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      sx={{
                        width: "90%",
                        height: "20px",
                        bgcolor: "#e8e8e8",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))
            : sortItems(list)?.map((row: any, index: number) => (
                <StyledTableRow
                  key={index}
                  onClick={(event) => {
                    dispatch(subReportsModalOpenAction());
                  }}
                >
                  <StyledTableCell align="left">{index + 1}</StyledTableCell>
                  <StyledTableCell align="left">
                    <Tooltip
                      arrow
                      placement="bottom"
                      title={row.name}
                      enterDelay={500}
                      enterNextDelay={1000}
                      leaveDelay={500}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            marginTop: "10px !important",
                          },
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          height: "20px",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          fontSize: "inherit",
                          color: "inherit",
                          [theme.breakpoints.down("md")]: {
                            height: "14px",
                          },
                        }}
                      >
                        {row.name}
                      </Typography>
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Tooltip
                      arrow
                      placement="bottom"
                      title={row.ip}
                      enterDelay={500}
                      enterNextDelay={1000}
                      leaveDelay={500}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            marginTop: "10px !important",
                          },
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          height: "20px",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          fontSize: "inherit",
                          color: "inherit",
                          [theme.breakpoints.down("md")]: {
                            height: "14px",
                          },
                        }}
                      >
                        {row.ip}
                      </Typography>
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <SvgIcon
                        component={
                          row.download === true
                            ? SquareCheckIcon
                            : SquareRemoveIcon
                        }
                        inheritViewBox
                        sx={{
                          width: "24px",
                          height: "24px",
                          color: row.download === true ? "#3AEC93" : "#E36464",
                          mr: "8px",
                          [theme.breakpoints.down("lg")]: {
                            width: "22px",
                            height: "22px",
                          },
                          [theme.breakpoints.down("md")]: {
                            width: "20px",
                            height: "20px",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "inherit",
                          color: "inherit",
                          lineHeight: "1.8",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.download === true ? "Ok" : "No"}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Typography
                      sx={{
                        fontSize: "inherit",
                        color: row.status === true ? "#3AEC93" : "#E36464",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.status ? "Viewed" : "Not Viewed"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Typography
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        height: "20px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "inherit",
                        color: "inherit",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    >
                      {`${row.timestamp.split("__")[0]} - ${
                        row.timestamp.split("__")[1]
                      }`}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Typography
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        height: "20px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "inherit",
                        color: "inherit",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    >
                      {row.browser?.name}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Typography
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        height: "20px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "inherit",
                        color: "inherit",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    >
                      {row.browser?.platform}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SubReportsModalTabel;
